<template>
  <div class="relative" ref="dropdownContainer">
    <!-- Trigger Button -->
    <button
      @click.stop="toggleDropdown"
      type="button"
      class="w-full px-2 py-1 border border-gray-300 rounded bg-white text-left focus:outline-none focus:ring-1 focus:ring-blue-dark hover:bg-gray-50 transition-colors flex items-center justify-between"
    >
      <span class="text-xs truncate" :class="{ sensitive: sensitiveLabels && modelValue?.length === 1 }">
        {{ displayText }}
      </span>
      <svg
        :class="['w-4 h-4 transition-transform ml-2 shrink-0', isOpen ? 'rotate-180' : '']"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
      </svg>
    </button>

    <!-- Dropdown Menu -->
    <Teleport to="body">
      <div
        v-show="isOpen"
        ref="dropdownMenu"
        class="fixed z-[9999] bg-white border border-gray-300 rounded-md shadow-lg overflow-y-auto"
        :style="{ ...dropdownStyle, maxHeight: resolvedMaxHeight + 'px' }"
      >
      <!-- Search + Select All / Clear All -->
      <div class="sticky top-0 bg-gray-50 border-b border-gray-200 px-3 py-2 flex flex-col gap-1.5">
        <input
          v-if="searchable"
          v-model="searchQuery"
          :class="{ sensitive: sensitiveLabels }"
          type="text"
          placeholder="Search..."
          class="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-dark"
          @click.stop
          @keydown.stop
        />
        <div class="flex items-center justify-between">
          <button
            @click.stop="selectAll"
            type="button"
            class="text-xs text-primary-600 hover:text-primary-800 font-medium"
          >
            Select All
          </button>
          <button
            @click.stop="clearAll"
            type="button"
            class="text-xs text-gray-600 hover:text-gray-800 font-medium"
          >
            Clear All
          </button>
        </div>
      </div>

      <!-- Options List -->
      <div class="py-1">
        <label
          v-for="option in filteredOptions"
          :key="option.value"
          class="flex items-center px-3 py-2 hover:bg-gray-100 cursor-pointer transition-colors"
          @click.stop
        >
          <input
            type="checkbox"
            :value="option.value"
            :checked="isSelected(option.value)"
            @change="toggleOption(option.value)"
            @click.stop
            class="h-4 w-4 text-primary-600 focus:ring-blue-dark border-gray-300 rounded cursor-pointer"
          />
          <span class="ml-3 text-xs text-gray-700" :class="{ sensitive: sensitiveLabels }">{{ option.label }}</span>
        </label>
      </div>

      <!-- Empty State -->
      <div v-if="filteredOptions.length === 0" class="px-3 py-4 text-center text-xs text-gray-500">
        No options available
      </div>
      </div>
    </Teleport>
  </div>
</template>

<script>
export default {
  name: 'SimpleMultiSelect',
  props: {
    sensitiveLabels: { type: Boolean, default: false },
    modelValue: {
      type: Array,
      default: () => []
    },
    options: {
      type: Array,
      required: true,
      // Expected format: [{ value: 'id', label: 'Display Name' }, ...]
    },
    placeholder: {
      type: String,
      default: 'Select options'
    },
    searchable: {
      type: Boolean,
      default: false
    },
    dropdownHeight: {
      type: Number,
      default: null
    }
  },
  emits: ['update:modelValue', 'change'],
  data() {
    return {
      uid: Math.random().toString(36).slice(2),
      isOpen: false,
      searchQuery: '',
      dropdownStyle: {
        top: '0px',
        left: '0px',
        width: '200px'
      }
    }
  },
  computed: {
    filteredOptions() {
      if (!this.searchable || !this.searchQuery) return this.options
      const q = this.searchQuery.toLowerCase()
      return this.options.filter(opt => opt.label.toLowerCase().includes(q))
    },
    resolvedMaxHeight() {
      return this.dropdownHeight || 240;
    },
    displayText() {
      if (!this.modelValue || this.modelValue.length === 0) {
        return this.placeholder
      }

      if (this.modelValue.length === 1) {
        const selectedOption = this.options.find(opt => opt.value === this.modelValue[0])
        return selectedOption ? selectedOption.label : this.placeholder
      }

      return `${this.modelValue.length} selected`
    }
  },
  methods: {
    updateDropdownPosition() {
      if (!this.$refs.dropdownContainer) return;

      const rect = this.$refs.dropdownContainer.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      const dropdownHeight = this.resolvedMaxHeight;

      let top = rect.bottom + 4;

      // If not enough space below, position above
      if (spaceBelow < dropdownHeight && rect.top > spaceBelow) {
        top = rect.top - dropdownHeight - 4;
      }

      this.dropdownStyle = {
        position: 'fixed',
        top: `${top}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        minWidth: '200px'
      };
    },
    toggleDropdown() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        document.dispatchEvent(new CustomEvent('simplemultiselect-open', { detail: this.uid }));
        this.$nextTick(() => {
          this.updateDropdownPosition();
        });
      }
    },
    closeDropdown() {
      this.isOpen = false
      this.searchQuery = ''
    },
    handleOtherOpen(e) {
      if (e.detail !== this.uid && this.isOpen) {
        this.closeDropdown();
      }
    },
    isSelected(value) {
      return this.modelValue.includes(value)
    },
    toggleOption(value) {
      let newValue
      if (this.isSelected(value)) {
        // Remove from selection
        newValue = this.modelValue.filter(v => v !== value)
      } else {
        // Add to selection
        newValue = [...this.modelValue, value]
      }

      this.$emit('update:modelValue', newValue)
      this.$emit('change', newValue)
    },
    selectAll() {
      const allValues = this.filteredOptions.map(opt => opt.value)
      // Merge with any already-selected values outside the filtered set
      const existing = this.modelValue.filter(v => !this.filteredOptions.find(o => o.value === v))
      const merged = [...existing, ...allValues]
      this.$emit('update:modelValue', merged)
      this.$emit('change', merged)
    },
    clearAll() {
      this.$emit('update:modelValue', [])
      this.$emit('change', [])
    },
    handleClickOutside(event) {
      const container = this.$refs.dropdownContainer;
      const menu = this.$refs.dropdownMenu;
      if (container && !container.contains(event.target) &&
          (!menu || !menu.contains(event.target))) {
        this.closeDropdown()
      }
    }
  },
  mounted() {
    document.addEventListener('click', this.handleClickOutside);
    document.addEventListener('simplemultiselect-open', this.handleOtherOpen);
    window.addEventListener('scroll', this.updateDropdownPosition, true);
    window.addEventListener('resize', this.updateDropdownPosition);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleClickOutside);
    document.removeEventListener('simplemultiselect-open', this.handleOtherOpen);
    window.removeEventListener('scroll', this.updateDropdownPosition, true);
    window.removeEventListener('resize', this.updateDropdownPosition);
  }
}
</script>

<style scoped>
/* Custom scrollbar for dropdown */
.overflow-y-auto::-webkit-scrollbar {
  width: 8px;
}

.overflow-y-auto::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb {
  background: #888;
  border-radius: 4px;
}

.overflow-y-auto::-webkit-scrollbar-thumb:hover {
  background: #555;
}
</style>
